import {Composer, Parser} from 'yaml';
import {dump} from 'js-yaml';
import {OrganizationModel} from '../models/resources/organization.model';
import {Const, ResourceServerState, ResourceState, ResourceType} from './Const';
import {AbstractControl, UntypedFormControl, Validators} from '@angular/forms';
import { FormStates } from '@fundamental-ngx/cdk/forms';
import {KeyValueModel} from '../models/misc/key-value/key-value.model';
import {ResourceModel} from '../models/resources/resource.model';
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {CascadeDeleteRule} from "../../service/services/misc/app-config/app-config.model";

export class Utils {

    static showPoliciesTemporaryFlag: boolean = false;
    static errorMessages: Map<string, string> = new Map([
        ["IDENT", Const.STRING_FORMAT_CORRECT_AND_INTERPOLATED]
    ]);
    static features: { [key: string]: string } = {};
    static isPrivateCloud: boolean;
    static hideOpscurity: boolean;
    static spfiEndpoint: string;
    static aiAssistant: boolean = false;
    static parseYamlFile(yamlFile: File, callback: (res: OrganizationModel) => void) {
        const parser = new Parser();
        const composer = new Composer();
        return yamlFile.text().then((res) => {
            const tokens = parser.parse(res);
            const docs = composer.compose(tokens);
            const obj: OrganizationModel = Array.from(docs, (doc) => doc.toJS())[0];
            callback(obj);
        });
    }

    static parseYamlToJson(yaml: string) {
        const parser = new Parser();
        const composer = new Composer();
        const tokens = parser.parse(yaml);
        const docs = composer.compose(tokens);
        return Array.from(docs, (doc) => doc.toJS())[0];
    }

    static initEditor(event: any, metadata: string, spec: string) {
        const session = event.getSession();
        this.setEditorDefaultState(metadata, spec, session);
    }

    static setEditorDefaultState(metaData: string, spec: string, session: any, isCollapse = false) {
        if (!session) {
            return;
        }
        session.foldAll();

        const lines = session.getLength();
        let continueLoop = true;

        for (let i = 0; i < lines && continueLoop; i++) {
            const line = session.getDisplayLine(i, 0, 0, 0);

            if (line === metaData) {
                session.unfold(i + 1);
            } else if (line === spec) {
                if (isCollapse) {
                    session.unfold(i);
                } else {
                    let index = i + 1;
                    while (index < lines && session.getDisplayLine(index, 0, 0, 0) === spec) {
                        index++;
                    }
                    for (let j = i; j < index; j++) {
                        session.unfold(j, true);
                    }
                    continueLoop = false;
                }
            }
        }
    }

    static parseGenericYamlFile(yamlFile: File, callback: (res: any) => void) {
        const parser = new Parser();
        const composer = new Composer();
        return yamlFile.text().then((res) => {
            try {
                const tokens = parser.parse(res);
                const docs = composer.compose(tokens);
                const obj: any = Array.from(docs, (doc) => doc.toString())[0];
                callback(obj);
            } catch (e) {
                throw e;
            }
        });
    }

    static rearrangeJsonOrder(jsonText: {[key: string]: string}, fields: string[]){
        try {
            const sortedJson = new Map<string, any>();
            const unSortedJson = new Map<string, any>();
            for (const field of fields) {
                if (jsonText.hasOwnProperty(field)) {
                    sortedJson.set(field, jsonText[field]);
                }
            }
            let details = Object.keys(jsonText);
            details.forEach((key) => {
                if (!fields.includes(key)) {
                    unSortedJson.set(key, jsonText[key]);
                }
            });
            const combinedJson: { [key: string]: string } = {};
            [...sortedJson, ...unSortedJson].forEach(([key, value]) => {
                combinedJson[key] = value;
            });
            return combinedJson;

        } catch (e) {
            return jsonText;
        }
    }
    static convertToYamlFile(jsonText: any) {
        return dump(jsonText, {
            lineWidth: -1,
        });
    }

    static getState(resource: ResourceModel, setUnknown: boolean): ResourceState {
        // First check if there is a deletion time, which means the resource is in a deleting phase
        if (resource?.resource?.metadata?.deletionTime) {
            return ResourceState.TERMINATING;
        }
        const conditions = resource.conditions;
        if (!conditions) {
            return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
        }

        let readyCondition = conditions.find((el) => el.type === Const.CONDITIONS.TYPES.READY);

        const readyState  = this.mapReadyReasonToState(readyCondition);
        if (readyState) {
            return readyState;
        }

        // Use fallback logic if there is no valid ready reason
        if (readyCondition?.observedGeneration && this.isGenerationUpdated(resource, readyCondition)) {
            if (readyCondition?.status === "True") {
                return ResourceState.FALLBACK_READY;
            } else {
                return readyCondition?.reason === ResourceState.IN_PROCESS ? ResourceState.IN_PROCESS : ResourceState.NOT_READY;
            }
        }

        if (!setUnknown && readyCondition?.observedGeneration && !this.isGenerationUpdated(resource, readyCondition)) {
            return ResourceState.IN_PROCESS;
        } else {
            return ResourceState.UNKNOWN;
        }
    }

    static mapReadyReasonToState(readyCondition: any): ResourceState | null {
        return ResourceServerState[readyCondition?.reason];
    }

    static isGenerationUpdated(resource: ResourceModel, readyCondition: any) {
        return !resource.resource ||
            resource?.resource?.metadata?.generation === readyCondition.observedGeneration;
    }
    static getFontAwesomeResourceGlyph(type: string): IconProp {
        return Const.FONT_AWESOME_RESOURCE_GLYPHS[type] || ['fas', 'cube'];
    }

    static getPolicyDisplayName(type: string) {
        return Const.POLICY_TYPE_DISPLAY_NAMES[type];
    }

    static getValidationState(control: AbstractControl | null): FormStates {
        if ((control?.touched || control?.dirty) && control?.invalid) {
            return 'error';
        }
        return null;
    }

    static convertLabels(labels: KeyValueModel[]): { [key: string]: string } {
        return Object.assign(
            {},
            ...labels.filter((el) => el.key.name !== '').map((el) => ({[el.key.name]: el.value.name}))
        );
    }

    static formatPath(path: string): string {
        if (path === Const.ROOT_PATH) {
            return '/ (root)';
        }
        return path;
    }

    static isMock() {
        return false;
    }

    static copyToClipboard(text: string) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text)
                .then(() => {
                    console.log('Text copied to clipboard');
                })
                .catch(err => {
                    console.error('Could not copy text: ', err);
                });
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';  // Avoid scrolling to bottom
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            try {
                const successful = document.execCommand('copy');
                const msg = successful ? 'successful' : 'unsuccessful';
                console.log(`Text copy attempt was ${msg}`);
            } catch (err) {
                console.error('Unable to copy text: ', err);
            } finally {
                document.body.removeChild(textarea);
            }
        }
    }

    static showPolicies(resource: ResourceModel) {
        return (
            !!resource &&
            (resource.type === ResourceType.ORGANIZATION ||
                resource.type === ResourceType.FOLDER ||
                resource.type === ResourceType.RESOURCE_GROUP) &&
                this.showPoliciesTemporaryFlag
        );
    }

    static isManagedService(resource: ResourceModel) {
        return (
            !!resource &&
            resource.type === ResourceType.MANAGED_SERVICE);
    }

    static hasFeatureFlag(flag: string): boolean {
        return !!Utils.getFeature(flag) && Utils.getFeature(flag) === true;
    }

    static id(): string {
        return '' + Math.random().toString(36).substr(2, 9);
    }

    static concatTypeAndName(resource: ResourceModel) {
        return resource.type + '-' + resource.name;
    }

    static setPrivateCloud(isPrivatecloud: boolean) {
        this.isPrivateCloud = isPrivatecloud;
    }

    static setHideOpscurity(hideOpscurity: boolean) {
        this.hideOpscurity = hideOpscurity;
    }

    static setAiAssistant(aiAssistant: boolean) {
        this.aiAssistant = !!aiAssistant;
    }

    /**
     * Double gate for the AI assistant feature:
     *  - Gate A: landscape config `frontendConfig.aiAssistant.enabled` (on everywhere except gov)
     *  - Gate B: `?AIAssistance=true` query param present in the browser URL
     */
    static isAiAssistantAvailable(): boolean {
        return this.aiAssistant && this.hasFeatureFlag(Const.FEATURE_AI_ASSISTANCE);
    }

    static setSpfiEndpoint(spfiEndpoint: string) {
        this.spfiEndpoint = spfiEndpoint;
    }

    static setFeature(feature: string, value: any) {
        this.features[feature] = value;
    }

    static setFeatureFlags(featureFlagsFromAppConfig: any) {
        if(featureFlagsFromAppConfig) {
            Object.entries(featureFlagsFromAppConfig).forEach(([k, v], index) => {
                this.setFeature(k, v);
            });
        }
        this.setFeatureFlagByQueryParam(Const.FEATURE_RUNTIME_POLICIES);
        this.setFeatureFlagByQueryParam(Const.FEATURE_SOFT_SELECT);
        this.setFeatureFlagByQueryParam(Const.FEATURE_ENTITLEMENT_POLICIES);
        this.setFeatureFlagByQueryParam(Const.FEATURE_BTP_WIZARD);
        this.setFeatureFlagByQueryParam(Const.FEATURE_UNIFIED_WIZARD_EUPORIE);
        this.setFeatureFlagByQueryParam(Const.FEATURE_CAD_SKIP_AUTO_ASSIGN_PLANS);
        this.setFeatureFlagByQueryParam(Const.FEATURE_CAD_DUAL_LANDSCAPE);
        this.setFeatureFlagByQueryParam(Const.FEATURE_MOVE_RESOURCE);
        this.setFeatureFlagByQueryParam(Const.FEATURE_STAGING_PARAMETERS);
        this.setFeatureFlagByQueryParam(Const.FEATURE_ADVANCED_SEARCH);
        this.setFeatureFlagByQueryParam(Const.FEATURE_AI_ASSISTANCE);
    }

    static setFeatureFlagByQueryParam(param: string) {
        if(location.hash.includes(param)) {
            this.setFeature(param, true);
        }
    }

    static getFeature(feature: string) : any {
        return this.features[feature];
    }

    static getFormControl(schemaProp?: any, val = "", required = true) {
        const validators = [];
        if (required) {
            validators.push(Validators.required);
        }
        if (schemaProp?.maxLength) {
            validators.push(Validators.maxLength(schemaProp.maxLength));
        }
        if (schemaProp?.minLength) {
            validators.push(Validators.minLength(schemaProp.minLength));
        }
        if (schemaProp?.pattern) {
            validators.push(Validators.pattern(schemaProp.pattern));
        }
        return new UntypedFormControl(val, validators);
    }

    static validateFormControl(formControl: AbstractControl): string {
        if (formControl.hasError('required')) {
            return 'This field is required';
        }

        if (formControl.hasError('minlength')) {
            const minLength = formControl.getError('minlength');
            return `Must not be less than ${minLength.requiredLength} characters`;
        }
        if (formControl.hasError('maxlength')) {
            const maxLength = formControl.getError('maxlength');
            return `Must not exceed ${maxLength.requiredLength} characters`;
        }
        if (formControl.hasError('pattern')) {
            const pattern = formControl.getError('pattern');
            return  pattern.message || `Invalid input. Allowed pattern: ${pattern.requiredPattern}`;
        }

        if (formControl.hasError('invalidName')) {
            return  `Invalid input. The value can contain only lowercase alphabetical letters (a-z), numbers (0-9), hyphens (-), and periods (.). It must start and end with a lowercase letter or number.`;
        }

        if (formControl.hasError('invalidStartsWith')) {
            const message = formControl.getError("invalidStartsWith").message;
            return  message || `Invalid input. Must start with: ${formControl.getError("invalidStartsWith").val}`;
        }

        if (formControl.hasError('disallowedValue')) {
            const message = formControl.getError("disallowedValue").message;
            return message;
        }

        if (formControl.hasError('duplicate')) {
            const message = formControl.getError("duplicate").message;
            return message;
        }
        return '';
    }

    static mapPathToResourcePaths(orgName: string, path: string): { name: string, path: string }[] {
        let split = path.split("/").filter(Boolean);

        let initialPath = orgName;

        return split.map(item => {
            const name = item;
            const path = initialPath;
            initialPath = `${initialPath}/${item}`;
            return { name, path };
        });
    }

    static enrichErrorMessage(message: string): string {
        // loop over the error messages map and if the message variable has the key in them, add the value to the message in a new line
        let enrichedMessage = "";
        for (const [key, value] of Utils.errorMessages.entries()) {
            if (message.includes(key)) {
                enrichedMessage = enrichedMessage + "\n" + value;
            }
        }
        return message + enrichedMessage;
    }

    static isPathCascadeDeleteable(rules: CascadeDeleteRule[], resource: ResourceModel): boolean {
        const resourceType = resource.type;
        try {
            const relevantRule = rules.find((rule: CascadeDeleteRule) => {
                return rule.parameters.type.toLowerCase() === resourceType.toLowerCase();
            })?.parameters;

            const cascadeDeleteablePaths: string[] = relevantRule?.allowedPaths || [];
            return cascadeDeleteablePaths.some(allowedPath => {
                if (resource.path === allowedPath) {
                    return true;
                }
                return resource.path.startsWith(allowedPath + '/');
            });
        } catch (e) {
            console.log("An error occurred while trying to check the cascade deleteable paths.");
            return false;
        }

    }

}
